import { Empty, Flex, Select, Tabs, TabsProps } from "antd";
import to from "await-to-js";
import { t } from "i18next";
import React, { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { useSearchParams } from "react-router-dom";
import { Button } from "../../../../components/Button/Button";
import { Input } from "../../../../components/Input/Input";
import { PopconfirmButton } from "../../../../components/PopconfirmButton/PopconfirmButton";
import { ModalsContext } from "../../../../context/modals.context";
import {
  formatOznakaSaKolicinomExcelCell,
  formatOznakaSaKolicinomPoAmbalaziExcelCell,
  formatOznakaSaKolicinomPoPakovanjuExcelCell,
  formatOznakaSaKolicinomPoRMExcelCell,
  formatOznakaSaKolicinomSerijskiBrojeviExcelCell,
} from "../../../../modules/obligee/periodicReports/periodicReport.functions";
import {
  initialState,
  PeriodicReportsContext,
} from "../../../../modules/obligee/periodicReports/periodicReports.context";
import { periodicReportsService } from "../../../../modules/obligee/periodicReports/periodicReports.service";
import {
  OznakaSaKolicinomPoRM,
  ReportType,
  TobaccoReportType,
} from "../../../../modules/obligee/periodicReports/periodicReports.types";
import { toastService } from "../../../../utils/toast.service";
import { writeExcel } from "../../../../utils/writeExcel";
import style from "./PeriodicReport.module.scss";
import { AlcoholTable } from "./components/AlcoholTable";
import { CoffeeTable } from "./components/CoffeeTable";
import TobaccoReport from "./components/TobaccoReport";

interface IPeriodicReportProps {
  /**
   * Type je indikator da li je MFIN ili ne
   */
  type?: boolean;
}

export const PeriodicReport: React.FC<IPeriodicReportProps> = ({ type }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialSelectedTab = () => {
    if (periodicReports.hasAlcoholCategory) return ReportType.ALCOHOL;

    if (
      periodicReports.hasTobaccoCategory?.hasCigarete ||
      periodicReports.hasTobaccoCategory?.hasNesagorevajuci
    )
      return ReportType.TOBACCO;

    return ReportType.COFFEE;
  };

  const defaultSubtab = () => {
    return periodicReports.hasTobaccoCategory?.hasCigarete
      ? TobaccoReportType.CIGARETE
      : TobaccoReportType.NESAGOREVAJUCI;
  };

  const { periodicReports, setPeriodicReports } = useContext(
    PeriodicReportsContext
  );

  const { setError } = useContext(ModalsContext);
  const [pib, setPib] = useState<string>("");
  const [periodFilter, setPeriodFilter] = useState<number | null>(null);
  const [selectedTab, setSelectedTab] = useState<any>(getInitialSelectedTab());
  const [selectedSubtab, setSelectedSubtab] = useState<TobaccoReportType>(
    defaultSubtab()
  );

  const fetchIzvestajIsporuceneMarkiceDuvan = useDebouncedCallback(async () => {
    const urlPeriod = searchParams.get("periodId");
    const urlPeriodId = urlPeriod ? parseInt(urlPeriod) : null;

    if (urlPeriodId) {
      setSearchParams({});
    }

    const [err, res] = await to(
      periodicReportsService.createIzvestajiIsporuceneMarkice({
        PeriodId: periodFilter,
        pib: pib,
      })
    );

    if (err || !res.success) return setError(res?.errorMessage);

    setPeriodicReports({
      tobaccoReport: res.data.tobaccoReport,
      coffeeReport: res.data.coffeeReport,
      alcoholReport: res.data.alcoholReport,
      hasTobaccoCategory: res.data.hasTobaccoCategory,
      hasCoffeeCategory: res.data.hasCoffeeCategory,
      hasAlcoholCategory: res.data.hasAlcoholCategory,
      showProcessButton: res.data.showProcessButton,
      repeatProcess: res.data.repeatProcess,
      period: res.data.period,
    });

    const isUrlPeriodIdValid = Boolean(
      urlPeriodId &&
        res.data?.period?.find((period) => period.id === urlPeriodId)
    );

    if (res.data?.period?.length && !periodicReports?.PeriodId) {
      onPeriodChangeHandler(
        isUrlPeriodIdValid ? urlPeriodId : res.data?.period?.[0]?.id
      );
    }
  }, 400);

  const tabs: TabsProps["items"] = [
    {
      key: ReportType.ALCOHOL,
      label: t("AKCIZNE_MARKICE.ALKOHOLNA_PICA"),
      children: <AlcoholTable />,
    },
    {
      key: ReportType.TOBACCO,
      label: t("AKCIZNE_MARKICE.DUVANSKI_PROIZVODI"),
      children: <TobaccoReport setType={setSelectedSubtab} />,
    },
    {
      key: ReportType.COFFEE,
      label: t("COMMON.KAFA"),
      children: <CoffeeTable />,
    },
  ];

  const filteredTabs = tabs.filter((tab) => {
    if (tab.key === ReportType.ALCOHOL && periodicReports.hasAlcoholCategory) {
      return true;
    } else if (
      tab.key === ReportType.TOBACCO &&
      (periodicReports.hasTobaccoCategory?.hasCigarete ||
        periodicReports.hasTobaccoCategory?.hasNesagorevajuci)
    ) {
      return true;
    } else if (
      tab.key === ReportType.COFFEE &&
      periodicReports.hasCoffeeCategory
    ) {
      return true;
    }
    return false;
  });

  const colDefAlcoholExcel = [
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_I_DATUM_ZAMA"),
      dataIndex: "brojIDatumZahteva",
    },

    {
      title: t("PERIODICNI_IZVESTAJI.SLOVNA_OZNAKA_I_KOLICINA"),
      dataIndex: "slovnaOznakaIKolicinaPreuzetihMarkica",
    },

    {
      title: t("PERIODICNI_IZVESTAJI.OZNAKA_SERIJE_I_SERIJSKI_BROJ"),
      dataIndex: "oznakaSerijeISerijskiBrojPreuzetihMarkica",
    },

    {
      title: t("PERIODICNI_IZVESTAJI.STANJE_NERAZDUZENIH_MARKICA"),
      dataIndex: "brojNeutrosenihMarkicaPrethodniKvartal",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },

    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_FLASA"),
      dataIndex: "brojIskoriscenihMarkicaIzProizvodnje",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },

    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_PROMET"),
      dataIndex: "brojIskoriscenihMarkicaIzUvoza",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.ZAPREMINA_FLASE"),
      dataIndex: "zapreminaAmbalaze",
      excelValueFormatter: formatOznakaSaKolicinomPoAmbalaziExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA"),
      dataIndex: "brojOstecenihVidljivihMarkica",
      excelValueFormatter: formatOznakaSaKolicinomSerijskiBrojeviExcelCell,
    },
    {
      title: t(
        "PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA_NEVIDLJIVI"
      ),
      dataIndex: "brojOstecenihNevidljivihMarkica",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.STANJE"),
      dataIndex: "brojNerazduzenihMarkicaISerijskiBrojNaKraju",
      excelValueFormatter: formatOznakaSaKolicinomSerijskiBrojeviExcelCell,
    },
  ];

  const colDefTobacco = [
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_I_DATUM_ZAMA"),
      dataIndex: "brojIDatumZahteva",
    },
    {
      title: t("PERIODICNI_IZVESTAJI.SLOVNA_OZNAKA_I_KOLICINA"),
      dataIndex: "slovnaOznakaIKolicinaPreuzetihMarkica",
    },
    {
      title: t("PERIODICNI_IZVESTAJI.OZNAKA_SERIJE_I_SERIJSKI_BROJ"),
      dataIndex: "oznakaSerijeISerijskiBrojPreuzetihMarkica",
    },
    {
      title: t("PERIODICNI_IZVESTAJI.STANJE_NERAZDUZENIH_MARKICA"),
      dataIndex: "brojNeutrosenihMarkicaPrethodniKvartal",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_DUVAN"),
      dataIndex: "brojIskoriscenihMarkicaIzProizvodnje",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_PROMET_DUVAN"),
      dataIndex: "brojIskoriscenihMarkicaIzUvoza",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.NAZIV_ROBNE_MARKE_CIGARETA"),
      dataIndex: "nazivRobneMarkeIMpcUvoz",
      excelValueFormatter: (value: OznakaSaKolicinomPoRM[] | null) =>
        formatOznakaSaKolicinomPoRMExcelCell(
          value,
          t("PERIODICNI_IZVESTAJI.RSD")
        ),
    },
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA"),
      dataIndex: "brojOstecenihVidljivihMarkica",
      excelValueFormatter: formatOznakaSaKolicinomSerijskiBrojeviExcelCell,
    },
    {
      title: t(
        "PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA_NEVIDLJIVI"
      ),
      dataIndex: "brojOstecenihNevidljivihMarkica",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.STANJE"),
      dataIndex: "brojNerazduzenihMarkicaISerijskiBrojNaKraju",
      excelValueFormatter: formatOznakaSaKolicinomSerijskiBrojeviExcelCell,
    },
  ];

  const colDefCoffeeExcel = [
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_I_DATUM_ZAMK"),
      dataIndex: "brojIDatumZahteva",
    },
    {
      title: t("PERIODICNI_IZVESTAJI.SLOVNA_OZNAKA_I_KOLICINA_KAFA"),
      dataIndex: "slovnaOznakaIKolicinaPreuzetihMarkica",
    },
    {
      title: t("PERIODICNI_IZVESTAJI.OZNAKA_SERIJE_I_SERIJSKI_BROJ_KAFA"),
      dataIndex: "oznakaSerijeISerijskiBrojPreuzetihMarkica",
    },
    {
      title: t("PERIODICNI_IZVESTAJI.STANJE_NERAZDUZENIH_MARKICA"),
      dataIndex: "brojNeutrosenihMarkicaPrethodniKvartal",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },

    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_KAFA"),
      dataIndex: "brojIskoriscenihMarkicaIzProizvodnje",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_ISKORISCENIH_MARKICA_PROMET_KAFA"),
      dataIndex: "brojIskoriscenihMarkicaIzUvoza",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.KOLICINA_KAFE_PREMA_VRSTI"),
      dataIndex: "pakovanjaAmbalaza",
      excelValueFormatter: formatOznakaSaKolicinomPoPakovanjuExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA_KAFA"),
      dataIndex: "brojOstecenihVidljivihMarkica",
      excelValueFormatter: formatOznakaSaKolicinomSerijskiBrojeviExcelCell,
    },
    {
      title: t(
        "PERIODICNI_IZVESTAJI.BROJ_VRACENIH_OSTECENIH_MARKICA_NEVIDLJIVI_KAFA"
      ),
      dataIndex: "brojOstecenihNevidljivihMarkica",
      excelValueFormatter: formatOznakaSaKolicinomExcelCell,
    },
    {
      title: t("PERIODICNI_IZVESTAJI.STANJE_KAFA"),
      dataIndex: "brojNerazduzenihMarkicaISerijskiBrojNaKraju",
      excelValueFormatter: formatOznakaSaKolicinomSerijskiBrojeviExcelCell,
    },
  ];

  const getXlsxDataSet = (tab: string) => {
    if (tab === ReportType.ALCOHOL)
      return {
        data: periodicReports.alcoholReport,
        colDefs: colDefAlcoholExcel,
        title: "alkohol_periodicni_izvestaj",
      };

    if (
      tab === ReportType.TOBACCO &&
      selectedSubtab === TobaccoReportType.CIGARETE
    )
      return {
        data: periodicReports.tobaccoReport?.cigareteReport,
        colDefs: colDefTobacco,
        title: "cigarete_periodicni_izvestaj",
      };

    if (
      tab === ReportType.TOBACCO &&
      selectedSubtab === TobaccoReportType.NESAGOREVAJUCI
    )
      return {
        data: periodicReports.tobaccoReport?.nesagorevajuciReport,
        colDefs: colDefTobacco,
        title: "nesagorevajuci_duvan_periodicni_izvestaj",
      };

    return {
      data: periodicReports.coffeeReport,
      colDefs: colDefCoffeeExcel,
      title: "kafa_periodicni_izvestaj",
    };
  };

  const getDatumIVremeGenerisanja = () => {
    if (selectedTab === ReportType.ALCOHOL) {
      return periodicReports.alcoholReport?.[0]
        ?.datumIVremeGenerisanjaIzvestaja;
    }

    if (selectedTab === ReportType.COFFEE) {
      return periodicReports.coffeeReport?.[0]?.datumIVremeGenerisanjaIzvestaja;
    }

    if (selectedTab === ReportType.TOBACCO) {
      if (selectedSubtab === TobaccoReportType.CIGARETE) {
        return periodicReports.tobaccoReport?.cigareteReport?.[0]
          ?.datumIVremeGenerisanjaIzvestaja;
      }
      return periodicReports.tobaccoReport?.nesagorevajuciReport?.[0]
        ?.datumIVremeGenerisanjaIzvestaja;
    }

    return "";
  };

  const handleExportExcelAlkohol = () => {
    const excelDataSet = getXlsxDataSet(
      selectedTab ? selectedTab : getInitialSelectedTab()
    );

    const datum = getDatumIVremeGenerisanja();

    writeExcel(
      excelDataSet.data ?? [],
      excelDataSet.colDefs,
      excelDataSet.title,
      `${t("AKCIZNE_MARKICE.PERIOD")}: ${
        periodicReports.period.find((period) => period.id === periodFilter)
          ?.naziv
      }, ${t("PERIODICNI_IZVESTAJI.DATUM_I_VREME_GENERISANJA")}: ${
        datum ? datum : "/"
      }`
    );
  };

  const handleGenerateReport = async () => {
    const [err, res] = await to(
      periodicReportsService.generatePeriodicReport()
    );

    const message = periodicReports.repeatProcess
      ? t("TOAST.USPESNO_REGENERISAN_PERIODICNI_IZVESTAJ")
      : t("TOAST.USPESNO_GENERISAN_PERIODICNI_IZVESTAJ");

    if (!err && res?.success) {
      toastService.success(message);
      await fetchIzvestajIsporuceneMarkiceDuvan();
    } else if (!err && !res?.success) {
      toastService.error(res?.errorMessage || t("COMMON.DOSLO_JE_DO_GRESKE"));
    }
  };

  const onPeriodChangeHandler = (value: number | null) => {
    setPeriodFilter(value);
    setPeriodicReports({ PeriodId: value });
  };

  useEffect(() => {
    setSelectedTab(getInitialSelectedTab());
  }, [
    periodicReports.hasAlcoholCategory,
    periodicReports.hasCoffeeCategory,
    periodicReports.hasTobaccoCategory,
  ]);

  useEffect(() => {
    // The first time MFIN lands on this page data shouldn't be fetched OR if PIB filter is cleared
    const isMfinWithEmptyPIB = type && !pib;

    if (isMfinWithEmptyPIB && periodFilter) {
      setPeriodicReports(initialState());
    }

    if (!isMfinWithEmptyPIB) {
      fetchIzvestajIsporuceneMarkiceDuvan();
    }
  }, [periodFilter, pib]);

  useEffect(() => {
    const urlPib = searchParams.get("pib");

    if (urlPib) {
      setPib(urlPib);
    }
  }, [searchParams]);

  useEffect(() => {
    return () => {
      setPeriodicReports(initialState());
    };
  }, []);

  return (
    <div className={style.mainContainer}>
      <div id="periodic">
        <h2>{t("PERIODICNI_IZVESTAJI.PERIODICNI_IZVESTAJI")}</h2>
        <Flex gap={20} align="center" style={{ paddingBottom: 10 }}>
          <Select
            className={style.fontSize}
            popupClassName={style.fontSize}
            value={periodicReports.PeriodId}
            onChange={onPeriodChangeHandler}
            options={periodicReports.period?.map((period) => ({
              label: period.naziv,
              value: period.id,
            }))}
            placeholder={t("AKCIZNE_MARKICE.PERIOD")}
            style={{ width: "30%" }}
          />
          {type ? (
            <Input
              allowClear
              className="w-20"
              onChange={(event) => setPib(event.target.value)}
              placeholder={t("PLACEHOLDERS.PIB")}
              name="pib-periodicni-izvjestaji"
              value={pib}
            />
          ) : null}
          <Button name="excel-export-exit" onClick={handleExportExcelAlkohol}>
            {t("COMMON.IZVEZI_U_EXCEL")}
          </Button>
          {(periodicReports.showProcessButton ||
            periodicReports.repeatProcess) &&
          !type ? (
            <PopconfirmButton
              buttonName="genirisi-periodicni-izvestaj"
              buttonText={
                periodicReports.repeatProcess
                  ? t("AKCIZNE_MARKICE.PONOVI_GENERISANJE_ZA_PERIOD")
                  : t("AKCIZNE_MARKICE.GENERISI_PERIODICNI_IZVESTAJ")
              }
              title={
                periodicReports.repeatProcess
                  ? t(
                      "AKCIZNE_MARKICE.PONOVO_GENERISI_PERIODICNI_IZVESTAJ_POTVRDA"
                    )
                  : t("AKCIZNE_MARKICE.GENERISI_PERIODICNI_IZVESTAJ_POTVRDA")
              }
              onConfirm={handleGenerateReport}
            />
          ) : null}
        </Flex>
      </div>
      {filteredTabs?.length ? (
        <Tabs
          items={filteredTabs}
          activeKey={selectedTab}
          onChange={setSelectedTab}
          destroyInactiveTabPane
        />
      ) : (
        <Flex align="center" justify="center" style={{ padding: 40 }}>
          <Empty
            description={
              !pib && type
                ? t("PERIODICNI_IZVESTAJI.UNESITE_PIB_PRIVREDNOG_SUBJEKTA")
                : t("COMMON.NEMA_PODATAKA")
            }
          />
        </Flex>
      )}
    </div>
  );
};
